import { lazy, Suspense, useRef } from 'react';
import { Panel } from 'react-resizable-panels';
import { LoadingSpinner } from '@repo/atomic-components';
import { classNames } from 'primereact/utils';
import { AppRoutes } from '@/constants';
import type { ChatDocumentPanelSettings } from '@/store/slices/chat/chat';
import type { CitationLinkTarget } from './DocumentEditor/DocumentEditor.tsx';
import type { DraftProjectSave } from './DocumentEditor/SaveToProjectButton';
import { Header } from '@/ui/components/DocumentViewer/VirtualizedPdfViewer/Header.tsx';
import { useDocViewerScrollAtBottom } from '@/ui/components/DocumentViewer/useDocViewerScrollAtBottom';

const DocumentEditor = lazy(() =>
  import('./DocumentEditor/DocumentEditor.tsx').then((module) => ({ default: module.DocumentEditor })),
);
import { DocumentViewer } from '@/ui/components/DocumentViewer';
import { getDocumentViewerKind } from '@/utils/getDocumentViewerKind';

export type ChatDocumentSidePanelProps = {
  /** Panel id, for a parent that sets the group's layout. */
  id?: string;
  documentSettings: ChatDocumentPanelSettings;
  fileUrl: string;
  documentName?: string;
  documentSizeMb?: number | null;
  draftContent?: string;
  onClose: () => void;
  onDownload?: () => void;
  isDownloading?: boolean;
  onCitationLinkClick?: (target: CitationLinkTarget) => void;
  /** Replaces what a click on a draft citation pill does. */
  onCitationPillClick?: (target: CitationLinkTarget, number: number | null) => void;
  /** documentId -> documentName lookup used by the draft's citation hover cards. */
  documentNameById?: Map<string, string>;
  /** Draft citation numbers whose extract is a model-written image description. */
  aiImageDescriptionNumbers?: ReadonlySet<number>;
  onDraftContentChange?: (content: string) => void;
  /** Shows the draft without editing, for users who can only view the chat. */
  isDraftReadOnly?: boolean;
  draftSaveStatus?: 'idle' | 'saving' | 'saved' | 'error';
  draftLastSavedAt?: Date | null;
  draftProjectSave?: DraftProjectSave;
  /** Adds the alignment buttons to the draft editor. */
  showDraftAlignment?: boolean;
  /** Narrower minimum, for when the draft and a cited document share the space. */
  minSize?: number;
  /** Chat search query to highlight in the draft. */
  draftSearchQuery?: string;
  /** The draft's match that is the chat search's active one, or -1. */
  draftActiveSearchMatchIndex?: number;
  onDraftSearchMatchCountChange?: (count: number) => void;
};

export function ChatDocumentSidePanel({
  id,
  documentSettings,
  fileUrl,
  documentName,
  documentSizeMb,
  draftContent,
  onClose,
  onDownload,
  isDownloading,
  onCitationLinkClick,
  onCitationPillClick,
  documentNameById,
  aiImageDescriptionNumbers,
  onDraftContentChange,
  isDraftReadOnly,
  draftSaveStatus,
  draftLastSavedAt,
  draftProjectSave,
  showDraftAlignment,
  minSize = 410,
  draftSearchQuery,
  draftActiveSearchMatchIndex,
  onDraftSearchMatchCountChange,
}: ChatDocumentSidePanelProps) {
  const { documentId, createdByAi, citationExtractPreview, citationPanelTitle, highlightText } = documentSettings;
  const documentPath = `/${AppRoutes.DocumentViewer}/${documentId}`;
  const headerTitle = citationExtractPreview ? (citationPanelTitle ?? documentName ?? '') : documentName || '';
  const kindFromName = getDocumentViewerKind({ documentName });
  const documentFormatForViewer = kindFromName === 'unknown' ? 'pdf' : undefined;
  const panelRef = useRef<HTMLDivElement>(null);
  const isAtBottom = useDocViewerScrollAtBottom(panelRef);

  return (
    <Panel id={id} minSize={minSize} defaultSize="43%">
      <div ref={panelRef} className="group relative flex h-full flex-col overflow-hidden">
        <div aria-hidden className={classNames('doc-viewer-fade-overlay', isAtBottom && 'at-bottom')} />
        {createdByAi && (
          <Suspense
            fallback={
              <div className="flex h-full w-full items-center justify-center">
                <LoadingSpinner />
              </div>
            }
          >
            <DocumentEditor
              documentPath={documentPath}
              title={documentName}
              initialContent={draftContent}
              onClose={onClose}
              onDownload={onDownload}
              isDownloading={isDownloading}
              onCitationLinkClick={onCitationLinkClick}
              onCitationPillClick={onCitationPillClick}
              documentNameById={documentNameById}
              aiImageDescriptionNumbers={aiImageDescriptionNumbers}
              onChange={onDraftContentChange}
              readOnly={isDraftReadOnly}
              saveStatus={draftSaveStatus}
              lastSavedAt={draftLastSavedAt}
              projectSave={draftProjectSave}
              showAlignment={showDraftAlignment}
              searchQuery={draftSearchQuery}
              activeSearchMatchIndex={draftActiveSearchMatchIndex}
              onSearchMatchCountChange={onDraftSearchMatchCountChange}
            />
          </Suspense>
        )}
        {!createdByAi &&
          (citationExtractPreview && highlightText ? (
            <>
              <Header
                documentName={headerTitle}
                documentPath={documentPath}
                onClose={onClose}
                viewerNameQueryParam={documentName}
                viewerSizeMbQueryParam={documentSizeMb}
              />
              <div className="border-theme-border-grey bg-theme-background-grey text-theme-black hide-scrollbar min-h-0 flex-1 overflow-y-auto border-t p-4 pt-14 text-sm leading-5">
                <div className="border-theme-border-grey bg-theme-white rounded-md border p-4 break-words whitespace-pre-wrap">
                  {highlightText}
                </div>
              </div>
            </>
          ) : (
            <>
              <Header
                documentName={documentName || ''}
                documentPath={documentPath}
                onClose={onClose}
                viewerSizeMbQueryParam={documentSizeMb}
              />
              <DocumentViewer
                documentId={documentId}
                fileUrl={fileUrl}
                documentName={documentName}
                documentFormat={documentFormatForViewer}
                documentSizeMb={documentSizeMb}
              />
            </>
          ))}
      </div>
    </Panel>
  );
}
